import { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';

interface Props {
  data: Record<string, number>;
  secondaryData?: Record<string, number>;
  label?: string;
  secondaryLabel?: string;
  onStateClick?: (stateCode: string) => void;
}

const US_ATLAS_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

const FIPS_TO_STATE: Record<string, string> = {
  '01':'AL','02':'AK','04':'AZ','05':'AR','06':'CA','08':'CO','09':'CT','10':'DE',
  '11':'DC','12':'FL','13':'GA','15':'HI','16':'ID','17':'IL','18':'IN','19':'IA',
  '20':'KS','21':'KY','22':'LA','23':'ME','24':'MD','25':'MA','26':'MI','27':'MN',
  '28':'MS','29':'MO','30':'MT','31':'NE','32':'NV','33':'NH','34':'NJ','35':'NM',
  '36':'NY','37':'NC','38':'ND','39':'OH','40':'OK','41':'OR','42':'PA','44':'RI',
  '45':'SC','46':'SD','47':'TN','48':'TX','49':'UT','50':'VT','51':'VA','53':'WA',
  '54':'WV','55':'WI','56':'WY',
};

const STATE_NAMES: Record<string, string> = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',
  CT:'Connecticut',DE:'Delaware',DC:'District of Columbia',FL:'Florida',GA:'Georgia',
  HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',
  LA:'Louisiana',ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',
  MS:'Mississippi',MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',
  NJ:'New Jersey',NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',
  OH:'Ohio',OK:'Oklahoma',OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',
  SD:'South Dakota',TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',
  WA:'Washington',WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',
};

export default function UsMap({ data, secondaryData, label = 'Items', secondaryLabel, onStateClick }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; state: string; val: number; val2?: number } | null>(null);

  const maxVal = useMemo(() => Math.max(1, ...Object.values(data)), [data]);

  const colorScale = useMemo(() =>
    d3.scaleSequential((t: number) => d3.interpolateRgb('#1a2e10', '#80ed3f')(t)).domain([0, maxVal]),
    [maxVal]
  );

  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    if (!tooltip) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTooltip(prev => prev ? { ...prev, x: event.clientX - rect.left + 12, y: event.clientY - rect.top - 10 } : null);
  }, [tooltip]);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 500;
    const height = 320;
    svg.attr('viewBox', `0 0 ${width} ${height}`);

    const projection = d3.geoAlbersUsa().fitSize([width, height], { type: 'Sphere' } as any);
    const path = d3.geoPath().projection(projection);

    fetch(US_ATLAS_URL)
      .then(r => r.json())
      .then(us => {
        const states = (topojson as any).feature(us, us.objects.states).features;

        svg.append('g')
          .selectAll('path')
          .data(states)
          .enter()
          .append('path')
          .attr('d', path as any)
          .attr('fill', (d: any) => {
            const stateCode = FIPS_TO_STATE[d.id] || FIPS_TO_STATE[String(d.id).padStart(2, '0')];
            const val = stateCode ? (data[stateCode] || 0) : 0;
            return val > 0 ? colorScale(val) : '#1a1e1a';
          })
          .attr('stroke', '#262b24')
          .attr('stroke-width', 0.5)
          .attr('cursor', 'pointer')
          .on('mouseenter', function (event: any, d: any) {
            const stateCode = FIPS_TO_STATE[d.id] || FIPS_TO_STATE[String(d.id).padStart(2, '0')];
            d3.select(this).attr('stroke', '#80ed3f').attr('stroke-width', 1.5).raise();
            if (stateCode) {
              const rect = containerRef.current?.getBoundingClientRect();
              if (rect) {
                setTooltip({
                  x: event.clientX - rect.left + 12,
                  y: event.clientY - rect.top - 10,
                  state: stateCode,
                  val: data[stateCode] || 0,
                  val2: secondaryData ? (secondaryData[stateCode] || 0) : undefined,
                });
              }
            }
          })
          .on('mouseleave', function () {
            d3.select(this).attr('stroke', '#262b24').attr('stroke-width', 0.5);
            setTooltip(null);
          })
          .on('click', (_event: any, d: any) => {
            const stateCode = FIPS_TO_STATE[d.id] || FIPS_TO_STATE[String(d.id).padStart(2, '0')];
            if (stateCode && onStateClick) onStateClick(stateCode);
          });

        const legendWidth = 200;
        const legendHeight = 8;
        const lx = width - legendWidth - 20;
        const ly = height - 25;

        const defs = svg.append('defs');
        const gradient = defs.append('linearGradient').attr('id', `pi-map-gradient-${label}`);
        gradient.append('stop').attr('offset', '0%').attr('stop-color', '#1a1e1a');
        gradient.append('stop').attr('offset', '100%').attr('stop-color', '#80ed3f');

        svg.append('rect').attr('x', lx).attr('y', ly)
          .attr('width', legendWidth).attr('height', legendHeight)
          .attr('rx', 3).attr('fill', `url(#pi-map-gradient-${label})`);

        svg.append('text').attr('x', lx).attr('y', ly + legendHeight + 12)
          .attr('font-size', 9).attr('fill', '#6b7366').text('0');
        svg.append('text').attr('x', lx + legendWidth).attr('y', ly + legendHeight + 12)
          .attr('font-size', 9).attr('fill', '#6b7366').attr('text-anchor', 'end').text(String(maxVal));
      });
  }, [data, colorScale, maxVal, label, secondaryData, onStateClick]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }} onMouseMove={handleMouseMove}>
      <svg ref={svgRef} style={{ width: '100%', height: 'auto' }} />
      {tooltip && (
        <div className="pi-map-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
          <div className="tooltip-state">{STATE_NAMES[tooltip.state] || tooltip.state}</div>
          <div className="tooltip-detail">{label}: {tooltip.val}</div>
          {secondaryLabel && tooltip.val2 !== undefined && (
            <div className="tooltip-detail">{secondaryLabel}: {tooltip.val2}</div>
          )}
        </div>
      )}
    </div>
  );
}
